import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { OfflineSyncService } from '../../../Services/offline/offline-sync.service';
import { SyncStatus } from '../../../Models/offline.model';
import { SyncKonfliktDialogComponent } from '../sync-konflikt-dialog/sync-konflikt-dialog.component';

/**
 * Die kleine Anzeige in der Kopfleiste: steht die Verbindung, und ist alles
 * angekommen?
 *
 * Bewusst immer sichtbar, sobald es etwas zu sagen gibt. Die Frage „sind
 * meine Prüfungen schon beim Server?" ist auf einer Anlage die wichtigste
 * überhaupt – sie darf nicht drei Klicks tief liegen.
 *
 * Solange alles synchronisiert UND online ist, bleibt die Anzeige unauffällig
 * grau; sie soll die Kopfleiste nicht dauerhaft dominieren.
 */
@Component({
  selector: 'app-offline-status',
  templateUrl: './offline-status.component.html',
  styleUrls: ['./offline-status.component.css']
})
export class OfflineStatusComponent implements OnInit, OnDestroy {

  status: SyncStatus = {
    zustand: 'online',
    ausstehend: 0,
    konflikte: 0,
    fehlgeschlagen: 0,
    meldung: 'Alles synchronisiert'
  };

  private abo?: Subscription;

  constructor(
    private sync: OfflineSyncService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.abo = this.sync.syncStatus$.subscribe(s => (this.status = s));

    // Beim Start der Anwendung einmal versuchen. Genau hier holt der Monteur
    // nach, was am Vortag in der Halle liegen geblieben ist.
    void this.sync.synchronisiere('start');
  }

  ngOnDestroy(): void {
    this.abo?.unsubscribe();
  }

  /**
   * Alles in Ordnung und nichts offen? Dann bleibt die Anzeige zurückhaltend.
   * Sie verschwindet nicht ganz – ein grüner Punkt ist die Bestätigung, dass
   * nichts hängt.
   */
  get unauffaellig(): boolean {
    return this.status.zustand === 'online';
  }

  get icon(): string {
    switch (this.status.zustand) {
      case 'offline':
        return 'cloud_off';

      case 'laeuft':
        return 'sync';

      case 'ausstehend':
        return 'cloud_queue';

      case 'konflikt':
        return 'merge_type';

      case 'fehler':
        return 'sync_problem';

      default:
        return 'cloud_done';
    }
  }

  get cssKlasse(): string {
    return `status-${this.status.zustand}`;
  }

  /** Ausführlicher Text für den Tooltip. */
  get tooltip(): string {
    const teile = [this.status.meldung];

    // Die Begründung des Servers gehört hierher: „1 Änderung konnte nicht
    // synchronisiert werden" allein hilft niemandem weiter.
    if (this.status.letzterFehlertext) {
      teile.push(this.status.letzterFehlertext);
    }

    if (this.status.letzterSyncUtc) {
      teile.push(
        `Letzte Synchronisierung: ${new Date(this.status.letzterSyncUtc).toLocaleString('de-DE')}`
      );
    }

    if (this.status.zustand !== 'laeuft') {
      teile.push('Klicken zum Synchronisieren');
    }

    return teile.join('\n');
  }

  /**
   * Ein Klick löst je nach Lage das aus, was gerade ansteht: gibt es etwas zu
   * entscheiden, öffnet sich der Dialog, sonst wird synchronisiert.
   *
   * Auch bei endgültig abgelehnten Änderungen MUSS der Dialog aufgehen – dort
   * stehen die Meldung des Servers und der einzige Weg, sie loszuwerden.
   * Ohne das führte ein Klick nur zu einem weiteren Sync, der die abgelehnten
   * Einträge überspringt: die Anzeige bliebe rot, und niemand käme an den
   * Grund heran.
   */
  async klick(): Promise<void> {
    if (this.status.konflikte > 0 || this.status.fehlgeschlagen > 0) {
      this.zeigeKonflikte();
      return;
    }

    const ergebnis = await this.sync.synchronisiere('manuell');

    if (ergebnis.konflikte > 0 || ergebnis.fehlgeschlagen > 0) {
      this.zeigeKonflikte();
      return;
    }

    this.snackBar.open(
      ergebnis.ausstehend === 0
        ? 'Alles synchronisiert.'
        : ergebnis.meldung,
      'OK',
      { duration: 3000 }
    );
  }

  private zeigeKonflikte(): void {
    this.dialog
      .open(SyncKonfliktDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
        autoFocus: false
      })
      .afterClosed()
      .subscribe(() => void this.sync.aktualisiereStatus());
  }
}
